import React, {
  useEffect,
  useState,
  useRef,
  ClipboardEvent,
  MouseEvent,
  useCallback,
} from 'react';
import styles from '@/styles/components/EditText.module.css';
import btnStyle from '@ax2/common/styles/components/ActionButton.module.css';
import _ from 'lodash';
import {
  TbBold,
  TbItalic,
  TbUnderline,
  TbSuperscript,
  TbSubscript,
  // TbList,
  // TbListNumbers,
  TbClearFormatting,
} from 'react-icons/tb';
import { IoMdUndo, IoMdRedo } from 'react-icons/io';

export interface EditTextData {
  value: string | null;
  rteValue?: string | null;
  placeholder?: string;
  ref?: any;
  parentFocus?: boolean;
  onChange: (text: string | null, rteText: string | null) => void;
}

interface ActiveStyle {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  superscript: boolean;
  subscript: boolean;
  insertOrderedList: boolean;
  insertUnorderedList: boolean;
}

export const EditText: React.FC<EditTextData> = props => {
  const spanElement = useRef<HTMLSpanElement>(null);
  const [text, setText] = useState({
    t: props.value,
    rt: props.rteValue ? props.rteValue : props.value,
  });
  const [focus, setFocus] = useState(false);
  const [cmdActive, setCmdActive] = useState<ActiveStyle>({
    bold: false,
    italic: false,
    underline: false,
    superscript: false,
    subscript: false,
    insertOrderedList: false,
    insertUnorderedList: false,
  });

  useEffect(() => {
    if (spanElement.current) {
      spanElement.current.innerHTML = props.rteValue
        ? props.rteValue
        : props.value || '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spanElement]);

  useEffect(() => {
    // call onclick only after state updated
    if (
      props.value !== text.t ||
      (props.rteValue && props.rteValue !== text.rt)
    ) {
      props.onChange(text.t, text.rt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  useEffect(() => {
    // sync uncontrolled component if external state change
    // we need that becouse we cannot use state-controlled component here (https://github.com/facebook/react/issues/2047)
    if (
      spanElement.current &&
      (props.value !== text.t || (props.rteValue && props.rteValue !== text.rt))
    ) {
      spanElement.current.innerHTML = props.rteValue
        ? props.rteValue
        : props.value || '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value]);

  const qc = (cmd: string) => document.queryCommandState(cmd);
  // Function to check if the cursor position has changed and update the state
  const checkCursor = useCallback(() => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const ancestorContainer = range.commonAncestorContainer;
      if (
        spanElement.current &&
        spanElement.current.contains(ancestorContainer)
      ) {
        setCmdActive({
          bold: qc('bold'),
          italic: qc('italic'),
          underline: qc('underline'),
          superscript: qc('superscript'),
          subscript: qc('subscript'),
          insertOrderedList: qc('insertOrderedList'),
          insertUnorderedList: qc('insertUnorderedList'),
        });
      }
    }
  }, []);

  const onChange = () => {
    if (spanElement.current) {
      const rteValue = spanElement.current.innerHTML;
      const value = new DOMParser().parseFromString(rteValue || '', 'text/html')
        .body.innerText;

      setText(prev => ({
        ...prev,
        t: value && value.length > 0 ? value : null,
        rt: rteValue && rteValue.length > 0 ? rteValue : value,
      }));
    }
  };

  const debounceOnChange = useCallback(_.debounce(onChange, 100), []);

  const replaceSelectedText = (replacementText: string, event: any) => {
    let sel, range;
    if (window.getSelection) {
      event.preventDefault();
      sel = window.getSelection();
      if (sel) {
        range = sel.getRangeAt(0);
        range.deleteContents();
        let textNode = document.createTextNode(replacementText);
        range.insertNode(textNode);
        range = range.cloneRange();
        range.setStartAfter(textNode);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
  };

  const handlePaste = async (event: ClipboardEvent) => {
    event.preventDefault();
    const { clipboardData } = event;
    if (clipboardData) {
      try {
        const content = clipboardData.getData('text/plain');
        if (content) {
          replaceSelectedText(content, event);
          debounceOnChange();
        }
      } catch (error) {
        console.error('Error reading clipboard:', error);
      }
    }
  };

  const handleTag = (command: string, event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (spanElement.current) {
      document.execCommand(command, false, '');
      spanElement.current.focus();
      checkCursor();
      if (command === 'removeFormat') {
        const sel = window.getSelection();
        if (sel && sel.toString().length < 1) {
          for (const [key, value] of Object.entries(cmdActive)) {
            if (value === true) {
              document.execCommand(key, false, '');
              checkCursor();
            }
          }
        }
      }
    }
  };

  useEffect(() => {
    if (spanElement.current && props.parentFocus) {
      spanElement.current.focus();
    }
  }, [props.parentFocus]);

  useEffect(() => {
    document.addEventListener('selectionchange', checkCursor);
    return () => {
      document.removeEventListener('selectionchange', checkCursor);
    };
  }, [checkCursor]);

  const btnsGroup = [
    {
      icon: <IoMdUndo />,
      command: 'undo',
    },
    {
      icon: <IoMdRedo />,
      command: 'redo',
    },
    {
      icon: <TbBold />,
      command: 'bold',
    },
    {
      icon: <TbItalic />,
      command: 'italic',
    },
    {
      icon: <TbUnderline />,
      command: 'underline',
    },
    {
      icon: <TbSuperscript />,
      command: 'superscript',
    },
    {
      icon: <TbSubscript />,
      command: 'subscript',
    },
    // {
    //   icon: <TbListNumbers />,
    //   command: 'insertOrderedList',
    // },
    // {
    //   icon: <TbList />,
    //   command: 'insertUnorderedList',
    // },
    {
      icon: <TbClearFormatting />,
      command: 'removeFormat',
    },
  ];

  return (
    <div className={styles.wrapper}>
      <span
        contentEditable={true}
        suppressContentEditableWarning={true}
        className={styles.text}
        ref={spanElement}
        defaultValue={text.rt ? text.rt : undefined}
        placeholder={props.placeholder}
        onInput={() => debounceOnChange()}
        onPaste={handlePaste}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
      ></span>
      <span className={`${styles.buttonsGroup} ${focus ? styles.active : ''}`}>
        {btnsGroup &&
          btnsGroup.length > 0 &&
          btnsGroup.map(btn => {
            return (
              <button
                key={btn.command}
                className={`${
                  (btn.command === 'bold' && cmdActive.bold) ||
                  (btn.command === 'italic' && cmdActive.italic) ||
                  (btn.command === 'underline' && cmdActive.underline) ||
                  (btn.command === 'superscript' && cmdActive.superscript) ||
                  (btn.command === 'subscript' && cmdActive.subscript) ||
                  (btn.command === 'insertOrderedList' &&
                    cmdActive.insertOrderedList) ||
                  (btn.command === 'insertUnorderedList' &&
                    cmdActive.insertUnorderedList)
                    ? btnStyle.primaryContainer
                    : btnStyle.secondaryAltContainer
                } ${styles.btn}`}
                onMouseDown={(e: MouseEvent<HTMLButtonElement>) => {
                  handleTag(btn.command, e);
                }}
              >
                {btn.icon ? btn.icon : ''}
              </button>
            );
          })}
      </span>
    </div>
  );
};
